import { ProviderRegistry } from '@config/providers/provider.registry';
import useStore from '@store/store';
import { MessageInterface, ModelConfig } from '@type/chat';
import { Message, ProviderKey } from '@type/provider';
import { debug } from '@utils/debug';

interface TitleConfig {
  model: string;
  maxTokens: number;
  temperature: number;
}

const TITLE_CONFIGS: Record<ProviderKey, TitleConfig> = {
  anthropic: { 
    model: 'claude-3-5-haiku-20241022', 
    maxTokens: 50, 
    temperature: 0.3 
  },
  openai: { 
    model: 'gpt-3.5-turbo', 
    maxTokens: 50, 
    temperature: 0.3 
  },
  deepinfra: {
    model: 'meta-llama/Llama-3.3-70B-Instruct-Turbo',
    maxTokens: 50,
    temperature: 0.3
  }
};

const TITLE_SYSTEM_PROMPT = 'Generate a concise, descriptive title (5 words or less) for this conversation. Return only the title text with no quotes or additional explanation.';

export class TitleService {
  async generateTitle(
    providerKey: ProviderKey,
    messages: MessageInterface[],
    baseConfig: ModelConfig,
    chatIndex?: number
  ): Promise<void> {

    try {
      // Skip if not enough messages
      if (messages.length < 2) {
        debug.log('title', 'Not enough messages to generate title');
        return;
      }

      // Get current state
      const currentState = useStore.getState();
      const targetChatIndex = chatIndex !== undefined ? chatIndex : currentState.currentChatIndex;
      const currentChat = currentState.chats[targetChatIndex];
      
      if (!currentChat || currentChat.titleSet) {
        debug.log('title', 'Chat already has title or does not exist');
        return;
      }

      // Get title configuration
      const titleConfig = TITLE_CONFIGS[providerKey];
      if (!titleConfig) {
        throw new Error(`No title configuration for provider: ${providerKey}`);
      }

      // Get API key
      const apiKey = currentState.apiKeys[providerKey];
      if (!apiKey) {
        throw new Error(`No API key found for ${providerKey}`);
      }

      // Create title prompt
      const lastUserMessage = messages
        .slice()
        .reverse()
        .find(msg => msg.role === 'user')?.content.substring(0, 100) || '';
      
      const lastAssistantMessage = messages
        .slice()
        .reverse()
        .find(msg => msg.role === 'assistant')?.content.substring(0, 200) || '';

      const titlePrompt: Message[] = [
        { role: 'system', content: TITLE_SYSTEM_PROMPT },
        { 
          role: 'user', 
          content: `${TITLE_SYSTEM_PROMPT}\nUser: ${lastUserMessage}\nAssistant: ${lastAssistantMessage}`
        }
      ];

      // Get provider and format request
      const providerInstance = ProviderRegistry.getProvider(providerKey);
      const formattedRequest = providerInstance.formatRequest(titlePrompt, {
        ...baseConfig,
        model: titleConfig.model,
        max_tokens: titleConfig.maxTokens,
        temperature: titleConfig.temperature,
        stream: false
      });

      debug.log('title', 'Submitting title generation request');

      // Call API
      const response = await fetch(`/api/chat/${providerKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formattedRequest,
          apiKey
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} ${errorText}`);
      }

      const responseData = await response.json();

      // Extract and clean title
      let title = providerInstance.parseResponse(responseData);

      title = title
        .trim()
        .replace(/^["'`]|["'`]$/g, '')
        .replace(/["'`]/g, '')
        .replace(/^[^\w\s]|[^\w\s]$/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      if (!title) {
        title = 'New Conversation';
      }

      debug.log('title', `Generated title: "${title}"`);

      // Update store
      const latestState = useStore.getState();
      const updatedChats = [...latestState.chats];
      const targetChat = updatedChats[targetChatIndex];
      if (!targetChat) {
        return;
      }

      updatedChats[targetChatIndex] = {
        ...targetChat,
        title,
        titleSet: true
      };

      latestState.setChats(updatedChats);
      debug.log('title', 'Title updated successfully');

    } catch (error: unknown) {
      debug.error('title', 'Title generation failed:', error);
    }
  }
}